import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    ContentChildren,
    EventEmitter,
    Input,
    NgZone,
    OnDestroy,
    OnInit,
    Output,
    QueryList,
    SimpleChanges,
    ViewEncapsulation,
    inject
} from '@angular/core';
import { NavigationEnd } from '@angular/router';
import { App } from '@df/core/app';
import { Meta } from '@df/core/meta/meta';
import { Logger } from '@df/core/service/logger';
import { Subscription, of } from 'rxjs';
import SwiperCore, { Autoplay, EffectFade, Mousewheel, Navigation, Pagination, Scrollbar, Thumbs } from 'swiper';
import { SwiperComponent, SwiperSlideDirective } from 'swiper/angular';
import { CarouselSlideDirective } from './carousel-slide.directive';
import { Marquee, MarqueeEvents, MarqueeOptions } from './plugin/marquee';

SwiperCore.use([Mousewheel, EffectFade, Navigation, Pagination, Thumbs, Autoplay, Scrollbar, Marquee]);

SwiperCore.use([Mousewheel, EffectFade, Navigation, Pagination, Thumbs, Autoplay, Scrollbar, Marquee]);

@Component({
    selector: 'carousel, [carousel]',
    templateUrl: './carousel.component.html',
    styleUrls: ['./carousel.component.scss'],
    styles: [
        `
            carousel {
                display: block;
            }
        `
    ],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class CarouselComponent extends SwiperComponent implements OnInit, OnDestroy, AfterViewInit {
    @Input()
    marquee?: MarqueeOptions;

    @Input()
    preventInSsr: boolean = false;

    // eslint-disable-next-line @angular-eslint/no-output-rename
    @Output('marquee')
    s_marquee: EventEmitter<MarqueeEvents['marquee']> = new EventEmitter<any>();

    // eslint-disable-next-line @angular-eslint/no-output-rename
    @Output('marqueeStart')
    s_marqueeStart: EventEmitter<MarqueeEvents['marqueeStart']> = new EventEmitter<any>();

    // eslint-disable-next-line @angular-eslint/no-output-rename
    @Output('marqueeStop')
    s_marqueeStop: EventEmitter<MarqueeEvents['marqueeStop']> = new EventEmitter<any>();

    @ContentChildren(CarouselSlideDirective, { descendants: false, emitDistinctChangesOnly: true })
    set _slidesEl(value: QueryList<CarouselSlideDirective>) {
        this.slidesEl = value;
    }

    private subscription = new Subscription();

    private meta = inject(Meta);
    private ngZone = inject(NgZone);
    private logger = inject(Logger);
    private app = inject(App);

    override ngOnInit(): void {
        if (this.app.isPlatformBrowser) {
            super.ngOnInit();
            this.subscription.add(
                this.meta.routerEvents.subscribe(event => {
                    if (event instanceof NavigationEnd) {
                        if (!this.swiperRef) {
                            this.logger.error('Swiper ref is undefined!');
                        } else {
                            if (this.loop) {
                                this.swiperRef.slideToLoop(this.initialSlide || 0, 0);
                            } else {
                                this.swiperRef.slideTo(this.initialSlide || 0, 0);
                            }
                            if (this.autoplay) {
                                this.swiperRef.autoplay.start();
                            }
                        }
                    }
                })
            );
        }
    }

    override ngOnDestroy(): void {
        this.subscription?.unsubscribe();

        super.ngOnDestroy();
    }

    override initSwiper() {
        if (this.app.isPlatformBrowser) {
            super.initSwiper();

            if (this.swiperRef) {
                this.ngZone.runOutsideAngular(() => {
                    this.updateParameter('marquee', this.marquee);

                    this.swiperRef?.update();
                });
            }
        }
    }

    override updateSwiper(changedParams: SimpleChanges | any) {
        if (this.app.isPlatformBrowser) {
            this.ngZone.runOutsideAngular(() => {
                if (changedParams.loop && this.slidesEl?.length) {
                    if (this.loop && !this.loopedSlides) {
                        this.calcLoopedSlides();
                        if (this.swiperRef) {
                            this.swiperRef.loopedSlides = this.loopedSlides || null;
                        }
                        const slides = this.slidesEl.map((slide: SwiperSlideDirective, index: number) => {
                            slide.slideIndex = index;
                            slide.classNames = this.slideClass || '';
                            return slide;
                        });

                        const loopedSlides = this.loopedSlides || 0;

                        this.prependSlides = of(slides.slice(this.slidesEl.length - loopedSlides));
                        this.appendSlides = of(slides.slice(0, loopedSlides));
                    }
                }
            });

            super.updateSwiper(changedParams);
        }
    }

    protected canPrint() {
        return !this.preventInSsr || this.app.isPlatformBrowser;
    }

  protected  getSlideClass(slide: any, slideKey: string): string {
        return [slide.class, this.slideClass, slideKey !== '' ? this.slideDuplicateClass : undefined].filter(Boolean).join(' ');
    }

  protected  getSlideStyle(slide: any): string {
        return [slide.style, this.style].filter(Boolean).join('; ');
    }
}
